/**
 * 错误码 → 人话 + 下一步（总纲 §7-2 错误态、§7-4、§9 错误处理）。
 *
 * 后端错误码全集在 `internal/pkg/errx/errx.go` 与 spec/06 §1.3，只增不改；
 * `errors.test.ts` 扫描那两处，保证每个码在这张表里都有一行。
 *
 * 给人看哪句，按**码**定（页面按 code 分支，这里也按 code 定待遇，不猜句子）：
 *   1. 业务码（校验不过、重名、缺权限、三条前置……）：服务端那句本来就具体可行动
 *      （「手机号格式不对」「该手机号已是本企业成员」），**用服务端那句**；表里的 title 只在服务端
 *      没给话时兜底，`next` / `to` 是补充的下一步；
 *   2. 基础设施码（`internal` / `upstream_error` / `not_ready` / `telephony_unavailable`）：服务端那句多半是
 *      给工程师看的（「员工服务未装配」「读不到正文，请确认 Key 含 scripts:read_body」），**一律用表里的人话**；
 *      原话留在 `serverMessage`，只随 request_id 一起给客服排障，不当正文；
 *   3. 认不出的码：兜底文案 + request_id，让用户能复制着去问客服。
 */

export interface ErrorText {
  /** 一句人话：出了什么事 */
  title: string;
  /** 下一步怎么办（可空） */
  next?: string;
  /** 去哪里处理（站内路径，可空） */
  to?: string;
  /** 链接文字；给了 `to` 才有意义 */
  toText?: string;
  /** 再点一次大概率就好了：界面可以直接给「重试」 */
  retry?: boolean;
  /**
   * 服务端那句比表里更有用（业务码）。
   * true = 服务端给了话就用它、表里的 title 兜底；缺省 = 一律用表里的 title（基础设施码）。
   */
  preferServer?: boolean;
}

/** 从 ApiError 或任何形状相近的对象里取字段；不依赖 `@/api/client`，避免 client ↔ errors 互相 import。 */
export interface ErrorLike {
  code?: string | null | undefined;
  message?: string | null | undefined;
  httpStatus?: number | null | undefined;
  requestId?: string | null | undefined;
  details?: unknown;
}

/** 页面展示用的最终形态：title / next / to 已经按上面的规则定好，直接画。 */
export interface ErrorView extends Omit<ErrorText, 'preferServer'> {
  code: string;
  requestId: string | undefined;
  /** 服务端原话（可能是工程师话），只给客服排障，不当正文 */
  serverMessage: string | undefined;
  /** 这个码表里认不认识；不认识的码界面应当把 request_id 露出来 */
  known: boolean;
}

/* ========== 后端错误码（errx.go / spec 06 §1.3，一个码一行，只增不改） ========== */

export const ERROR_TEXT: Readonly<Record<string, ErrorText>> = {
  bad_request: {
    preferServer: true,
    title: '填写的内容有问题。',
    next: '检查一下再提交。',
  },
  validation_failed: {
    preferServer: true,
    title: '有一项没填对。',
    next: '按提示改一下再提交。',
  },
  unauthorized: {
    preferServer: true,
    title: '登录已过期。',
    next: '重新登录后继续。',
    to: '/login',
    toText: '去登录',
  },
  forbidden: {
    preferServer: true,
    title: '你没有这个操作的权限。',
    next: '找管理员开通，或换个有权限的账号。',
  },
  tenant_suspended: {
    preferServer: true,
    title: '这家企业已被停用。',
    next: '联系客服了解原因。',
  },
  membership_disabled: {
    preferServer: true,
    title: '你在这家企业的账号已被停用。',
    next: '找这家企业的管理员恢复。',
  },
  certification_incomplete: {
    preferServer: true,
    title: '还差企业认证。',
    next: '认证通过后 TA 才能打电话。',
    to: '/settings/company',
    toText: '去认证',
  },
  balance_insufficient: {
    preferServer: true,
    title: '余额不够了。',
    next: '充值后再试。',
    to: '/billing',
    toText: '去充值',
  },
  seat_fee_expired: {
    preferServer: true,
    title: '坐席费到期了。',
    next: '续上后 TA 才能继续工作。',
    to: '/billing/seats',
    toText: '去续费',
  },
  not_found: {
    preferServer: true,
    title: '找不到这条记录。',
    next: '可能已被删除，或你没有权限看；刷新试试。',
  },
  conflict: {
    preferServer: true,
    title: '这条记录刚被改过。',
    next: '刷新后再操作一次。',
  },
  employee_not_ready: {
    preferServer: true,
    title: 'TA 现在的状态不能做这个。',
    next: '先让 TA 休息一下再改。',
  },
  campaign_running: {
    preferServer: true,
    title: '这批名单正在打。',
    next: '先点「先不打了」，再做这个操作。',
  },
  lead_import_failed: {
    preferServer: true,
    title: '名单文件没读出来。',
    next: '按模板整理后重新上传；读不了的行可以下载。',
  },
  rate_limited: {
    preferServer: true,
    title: '操作太频繁了。',
    next: '歇几秒再试。',
    retry: true,
  },
  quota_exceeded: {
    preferServer: true,
    title: '超出今天的额度了。',
    next: '明天再试，或联系客服提额。',
  },
  internal: {
    title: '服务器开小差了。',
    next: '稍后再试；一直不行就把下面的编号发给客服。',
    retry: true,
  },
  upstream_error: {
    title: '电话平台那边出了点问题。',
    next: '稍后再试；一直不行就把下面的编号发给客服。',
    retry: true,
  },
  not_ready: {
    title: '服务暂时没准备好。',
    next: '等一分钟再试。',
    retry: true,
  },
  telephony_unavailable: {
    title: '电话线路暂时不可用。',
    next: '稍后再试，已保存的交代不受影响。',
    retry: true,
  },

  /* ---------- 前端自己造的码（没走到服务端 / 服务端没给 JSON） ---------- */

  network_error: {
    preferServer: true,
    title: '网络不通。',
    next: '检查网络后重试。',
    retry: true,
  },
  /** `api/endpoints/leads.ts` 上传走 XHR，断网用的是这个码 */
  network: {
    preferServer: true,
    title: '网络中断，没传完。',
    next: '检查网络后重新上传。',
    retry: true,
  },
  /** 同上，非 JSON 响应 */
  http_error: {
    preferServer: true,
    title: '请求没成功。',
    next: '稍后再试；一直不行就把下面的编号发给客服。',
    retry: true,
  },
};

/** 服务端没给 JSON 信封时 `client.ts` 造的码：`http_502` 这种。按状态段给话。 */
function httpStatusText(status: number): ErrorText {
  if (status === 429) return ERROR_TEXT['rate_limited'] as ErrorText;
  if (status === 401) return ERROR_TEXT['unauthorized'] as ErrorText;
  if (status === 403) return ERROR_TEXT['forbidden'] as ErrorText;
  if (status === 404) return ERROR_TEXT['not_found'] as ErrorText;
  if (status >= 500) return ERROR_TEXT['internal'] as ErrorText;
  if (status === 0) return ERROR_TEXT['network_error'] as ErrorText;
  return ERROR_TEXT['http_error'] as ErrorText;
}

const UNKNOWN: ErrorText = {
  title: '出了点问题。',
  next: '重试一下；还不行就把下面的编号发给客服。',
  retry: true,
};

/** 兜底文案；`errors.test.ts` 与界面都从这里拿，别在页面里再写一遍。 */
export const UNKNOWN_ERROR_TITLE = UNKNOWN.title;

const isApiErrorLike = (e: unknown): e is ErrorLike =>
  typeof e === 'object' && e !== null && ('code' in e || 'httpStatus' in e);

function lookup(code: string, status: number): { text: ErrorText; known: boolean } {
  const hit = ERROR_TEXT[code];
  if (hit) return { text: hit, known: true };
  if (/^http_\d{3}$/.test(code)) return { text: httpStatusText(status), known: true };
  return { text: UNKNOWN, known: false };
}

/**
 * 服务端那句是不是写给工程师看的。
 * 基础设施码的原话留在 `serverMessage` 里；页面想把它当小字补一句（比如「支付渠道下单失败，请改用对公转账」
 * 对用户有用）时，先用这个过一遍：命中「未装配」「Key 含 scripts:read_body」这类内部用词就别露出来。
 * 只认明确的内部用词，宁可放过也不误伤有用的句子。
 */
const TECHNICAL =
  /未装配|未接入|装配|仓储|签名密钥|封存密钥|scopes?[:：]|read_body|Key 含|该 Key|BindTenant|%[sdv]/;

export const looksTechnical = (msg: string): boolean => TECHNICAL.test(msg);

/**
 * 把一个错误翻成「给人看的」。
 * 传 ApiError、普通 Error、字符串、甚至 undefined 都行：不认识的一律走兜底。
 */
export function describeError(err: unknown): ErrorView {
  if (!isApiErrorLike(err)) {
    const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : undefined;
    return {
      code: 'unknown',
      title: UNKNOWN.title,
      ...(UNKNOWN.next ? { next: UNKNOWN.next } : {}),
      retry: true,
      requestId: undefined,
      serverMessage: msg?.trim() || undefined,
      known: false,
    };
  }

  const code = (err.code ?? '').trim() || 'unknown';
  const status = err.httpStatus ?? 0;
  const server = (err.message ?? '').trim() || undefined;
  const { text, known } = lookup(code, status);
  // 业务码 + 服务端给了话 → 用服务端的；基础设施码、没给话、认不出的码 → 用表里的
  const useServer = Boolean(known && text.preferServer && server);

  return {
    code,
    title: useServer && server ? server : text.title,
    ...(text.next ? { next: text.next } : {}),
    ...(text.to ? { to: text.to } : {}),
    ...(text.toText ? { toText: text.toText } : {}),
    ...(text.retry ? { retry: true } : {}),
    requestId: err.requestId?.trim() || undefined,
    serverMessage: server,
    known,
  };
}

/**
 * 一句话版本，给 toast / Alert 的 message 用。
 *
 * - 服务端那句够具体就只说那句（它通常自带下一步）；
 * - 用表里的人话时把下一步接在后面；
 * - 认不出的码把 request_id 直接写进去，toast 里也能看到、复制。
 */
export function errorText(err: unknown): string {
  const v = describeError(err);
  const fromServer = v.serverMessage !== undefined && v.title === v.serverMessage;
  let out = fromServer || !v.next ? v.title : `${v.title}${v.next}`;
  if (!v.known && v.requestId) out = `${out}（编号 ${v.requestId}）`;
  return out;
}

/** 给「复制编号」用：有 request_id 才有东西可复制。 */
export function errorRequestId(err: unknown): string | undefined {
  return describeError(err).requestId;
}
